<template>
  <div class="shop-panel" @keydown.esc.prevent="onEscape">
    <div class="shop-filter-wrap">
      <input
        ref="filterInput"
        v-model="query"
        type="text"
        class="shop-filter"
        placeholder="Filter"
        aria-label="Filter shop inventory"
        @keydown.esc.prevent="onEscape"
      />
      <button
        v-if="query"
        type="button"
        class="shop-filter-clear"
        title="Clear"
        aria-label="Clear filter"
        @click="clearFilter"
      >
        <i class="fas fa-times"></i>
      </button>
    </div>

    <p v-if="loading" class="shop-status">Loading shop inventory...</p>
    <p v-else-if="error" class="shop-status">{{ error }}</p>

    <div v-else-if="showMatches" class="shop-matches">
      <p v-if="query.trim() && !matches.length" class="shop-status">No matching items</p>
      <button
        v-for="(item, index) in matches"
        :key="item.id"
        type="button"
        class="shop-hit"
        :class="{ 'shop-hit-alt': index % 2 === 1 }"
        @click="selectItem(item)"
      >
        <span class="shop-hit-text">{{ item.preview }}</span>
      </button>
    </div>

    <div
      v-else
      class="shop-detail"
      @pointerdown="startItemSwipe"
      @pointermove="moveItemSwipe"
      @pointerup="endItemSwipe"
      @pointercancel="cancelItemSwipe"
    >
      <div class="shop-detail-scroll">
      <p class="shop-swipe-hint">swipe left and right to browse</p>
      <div
        class="shop-photo-frame"
        title="Hold to change the photo"
        @pointerdown="startPhotoPress"
        @pointermove="movePhotoPress"
        @pointerup="endPhotoPress"
        @pointercancel="endPhotoPress"
        @contextmenu.prevent
      >
        <img
          v-if="photoUrl"
          :src="photoUrl"
          class="shop-photo"
          alt="Shop item photo"
        />
        <span v-else class="shop-no-photo">no photo</span>
        <div
          v-if="photoUploading"
          class="shop-spinner-overlay"
          role="status"
        >
          <i class="fas fa-spinner fa-spin" aria-hidden="true"></i>
          <span>Uploading photo...</span>
        </div>
      </div>
      <div class="shop-id-row">
        <div class="shop-id-cell">
          <span>Item</span>
          <strong>{{ item.id || '—' }}</strong>
        </div>
        <label class="shop-id-cell shop-date-cell">
          <span>Inventory date</span>
          <input v-if="editing" v-model="draft.inventoryDate" type="date" />
          <strong v-else>{{ dateLabel || '—' }}</strong>
        </label>
      </div>
      <div class="qty-row">
        <button type="button" class="qty-btn" title="Decrease quantity" @click="changeQty(-1)">
          −
        </button>
        <input
          class="qty-input"
          inputmode="numeric"
          aria-label="Quantity"
          :value="draft.qty"
          @change="onQtyInput"
        />
        <button type="button" class="qty-btn" title="Increase quantity" @click="changeQty(1)">
          +
        </button>
      </div>

      <label class="shop-field">
        <span>Description</span>
        <textarea
          v-if="editing"
          v-model="draft.description"
          maxlength="256"
          rows="4"
        ></textarea>
        <p v-else class="shop-value">{{ item.description }}</p>
      </label>

      <div class="place-row">
        <label class="place-cell place-building">
          <span>Building</span>
          <select
            aria-label="Building"
            :value="draft.buildingId == null ? '' : String(draft.buildingId)"
            @change="onBuildingChange"
          >
            <option
              v-for="building in buildings"
              :key="building.id"
              :value="String(building.id)"
            >
              {{ building.label }}
            </option>
          </select>
        </label>
        <label class="place-cell">
          <span>Rack</span>
          <input
            v-if="editing"
            :value="draft.rack"
            inputmode="numeric"
            maxlength="2"
            @input="onPlaceInput('rack', $event.target.value)"
          />
          <strong v-else>{{ item.rack || '—' }}</strong>
        </label>
        <label class="place-cell">
          <span>Shelf</span>
          <input
            v-if="editing"
            :value="draft.shelf"
            inputmode="numeric"
            maxlength="2"
            @input="onPlaceInput('shelf', $event.target.value)"
          />
          <strong v-else>{{ item.shelf || '—' }}</strong>
        </label>
        <label class="place-cell">
          <span>Box</span>
          <input
            v-if="editing"
            :value="draft.box"
            inputmode="numeric"
            maxlength="2"
            @input="onPlaceInput('box', $event.target.value)"
          />
          <strong v-else>{{ item.box || '—' }}</strong>
        </label>
      </div>

      <label class="shop-field">
        <span>Notes</span>
        <textarea v-if="editing" v-model="draft.notes" rows="4"></textarea>
        <p v-else class="shop-value shop-notes">{{ item.notes }}</p>
      </label>

      </div>
      <div class="shop-dock">
        <p v-if="saveError" class="shop-status">{{ saveError }}</p>
        <button
          v-if="!editing"
          type="button"
          class="shop-action"
          @click="editing = true"
        >
          Edit
        </button>
        <div v-else class="shop-edit-actions">
          <button
            type="button"
            class="shop-action"
            :disabled="saving"
            :aria-busy="savingRecord"
            @click="saveItem"
          >
            <i v-if="savingRecord" class="fas fa-spinner fa-spin" aria-hidden="true"></i>
            {{ savingRecord ? 'Saving...' : 'Save' }}
          </button>
          <button
            v-if="item.id"
            type="button"
            class="shop-action shop-delete"
            :disabled="saving"
            @click="deleteItem"
          >
            Delete
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="photoLoader"
      class="shop-photo-loader"
      @click.self="photoLoader = false"
    >
      <div class="shop-photo-loader-card" role="dialog" aria-label="Photo">
        <label class="shop-photo-pick">
          <i class="fas fa-images"></i>
          Gallery
          <input
            type="file"
            accept="image/*,.heic,.heif,image/heic,image/heif"
            :disabled="photoBusy || photoUploading"
            @change="onPhotoPicked"
          />
        </label>
        <label class="shop-photo-pick">
          <i class="fas fa-camera"></i>
          Camera
          <input
            type="file"
            accept="image/*,.heic,.heif,image/heic,image/heif"
            capture="environment"
            :disabled="photoBusy || photoUploading"
            @change="onPhotoPicked"
          />
        </label>
        <button
          type="button"
          class="shop-photo-pick shop-photo-delete"
          :disabled="photoBusy || photoUploading || !canClearPhoto"
          @click="deletePhoto"
        >
          Delete
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import axios from '@/axiosConfig';
import {
  isLikelyImageFile,
  prepareImageForWorkOrderUpload,
  uploadFileLikeWorkOrder,
} from '@/utils/chunkedHeicUpload';
import {
  createPhotoCache,
  PHOTO_CACHE_LIMIT,
} from '@/utils/workOrderPhotoCache';
import {
  shopInventoryArrowDirection,
  shopInventoryDateLabel,
  shopInventoryDefaultItem,
  shopInventoryMatches,
  shopInventoryNeighbor,
  shopInventorySoleMatch,
  shopInventorySwipe,
  shopInventoryToday,
  shopPlaceCode,
  shopBuildingId,
  shopDefaultBuildingId,
  readShopPlaceMemory,
  writeShopPlaceMemory,
  shopQty,
  shopText,
  DESCRIPTION_LIMIT,
} from '@/utils/shopInventory';

const shopPhotos = createPhotoCache({ dbName: 'ShopInventoryPhotos' });

const items = ref([]);
const buildings = ref([]);
const query = ref('');
const loading = ref(false);
const error = ref('');
const saveError = ref('');
const saving = ref(false);
const savingRecord = ref(false);
const photoBusy = ref(false);
const photoUploading = ref(false);
const selectedId = ref(null);
const creating = ref(false);
const editing = ref(false);
const PHOTO_PRESS_MS = 600;
const photoUrl = ref('');
const pendingPhoto = ref(null);
const photoLoader = ref(false);
let photoPressTimer = null;
let photoPressStart = null;
let photoPressMoved = false;
let itemSwipe = null;
let photoLoadToken = 0;
const filterInput = ref(null);
const draft = ref(blankDraft());

const matches = computed(() => shopInventoryMatches(items.value, query.value));
let appliedQuery = '';
watch([matches, items, query], () => {
  if (editing.value || creating.value) return;
  const needle = String(query.value || '').trim();
  const queryChanged = needle !== appliedQuery;
  appliedQuery = needle;
  const only = shopInventorySoleMatch(matches.value);
  if (only) {
    if (Number(only.id) !== Number(selectedId.value)) selectItem(only);
    return;
  }
  if (!needle) {
    if (!selectedId.value) openDefaultItem();
    return;
  }
  if (queryChanged && selectedId.value) {
    selectedId.value = null;
    revokePhoto();
  }
});
const item = computed(
  () => items.value.find((row) => row.id === selectedId.value) || draft.value,
);
const showMatches = computed(() => !selectedId.value && !creating.value);
const dateLabel = computed(() => shopInventoryDateLabel(item.value.inventoryDate));
const canClearPhoto = computed(
  () => Boolean(photoUrl.value || pendingPhoto.value || item.value.hasPhoto),
);

function blankDraft(place) {
  const remembered = place || {};
  return {
    id: null,
    description: '',
    qty: 0,
    inventoryDate: shopInventoryToday(),
    buildingId: resolveDraftBuilding(remembered.buildingId),
    rack: placeCode(remembered.rack),
    shelf: placeCode(remembered.shelf),
    box: placeCode(remembered.box),
    notes: '',
    hasPhoto: false,
  };
}

function rememberNewPlace() {
  if (!creating.value) return;
  writeShopPlaceMemory(localStorage, draft.value);
}

function placeCode(value) {
  return shopPlaceCode(value);
}

function resolveDraftBuilding(value) {
  const id = shopBuildingId(value);
  if (!buildings.value.length) return id;
  if (id && buildings.value.some((row) => Number(row.id) === id)) return id;
  return shopDefaultBuildingId(buildings.value);
}

watch(buildings, () => {
  const resolved = resolveDraftBuilding(draft.value.buildingId);
  if (resolved !== draft.value.buildingId) {
    draft.value = { ...draft.value, buildingId: resolved };
  }
});

function focusFilter() {
  nextTick(() => {
    const input = filterInput.value;
    if (input && typeof input.focus === 'function') input.focus();
  });
}

function clearFilter() {
  query.value = '';
  focusFilter();
}

function clearPhotoPress() {
  if (photoPressTimer) {
    clearTimeout(photoPressTimer);
    photoPressTimer = null;
  }
}

function startPhotoPress(event) {
  if (photoBusy.value || photoUploading.value) return;
  if (event.pointerType === 'mouse' && event.button !== 0) return;
  photoPressMoved = false;
  photoPressStart = { x: event.clientX, y: event.clientY };
  clearPhotoPress();
  photoPressTimer = setTimeout(() => {
    photoPressTimer = null;
    if (!photoPressMoved) photoLoader.value = true;
  }, PHOTO_PRESS_MS);
}

function movePhotoPress(event) {
  if (!photoPressStart) return;
  const dx = Math.abs(event.clientX - photoPressStart.x);
  const dy = Math.abs(event.clientY - photoPressStart.y);
  if (dx > 10 || dy > 10) {
    photoPressMoved = true;
    clearPhotoPress();
  }
}

function endPhotoPress() {
  clearPhotoPress();
  photoPressStart = null;
}

function swipeOnControl(event) {
  const target = event.target;
  if (!target || typeof target.closest !== 'function') return false;
  return Boolean(target.closest('input, textarea, button, a, select'));
}

function startItemSwipe(event) {
  itemSwipe = null;
  if (editing.value || creating.value || !selectedId.value) return;
  if (photoLoader.value || saving.value || photoUploading.value || photoBusy.value) {
    return;
  }
  if (event.pointerType === 'mouse' && event.button !== 0) return;
  if (swipeOnControl(event)) return;
  itemSwipe = {
    x: event.clientX,
    y: event.clientY,
    t: Date.now(),
    pointerId: event.pointerId,
  };
}

function moveItemSwipe(event) {
  if (!itemSwipe || event.pointerId !== itemSwipe.pointerId) return;
  movePhotoPress(event);
}

function stepItem(direction) {
  if (!direction) return;
  if (editing.value || creating.value || !selectedId.value) return;
  if (photoLoader.value || saving.value || photoUploading.value || photoBusy.value) {
    return;
  }
  const neighbor = shopInventoryNeighbor(
    items.value,
    selectedId.value,
    direction,
  );
  if (!neighbor || Number(neighbor.id) === Number(selectedId.value)) return;
  selectItem(neighbor);
}

function endItemSwipe(event) {
  const start = itemSwipe;
  itemSwipe = null;
  if (!start || event.pointerId !== start.pointerId) return;
  const direction = shopInventorySwipe(
    event.clientX - start.x,
    event.clientY - start.y,
    { elapsed: Date.now() - start.t },
  );
  stepItem(direction);
}

function arrowInField(element) {
  if (!element || typeof element.closest !== 'function') return false;
  return Boolean(
    element.closest('input, textarea, select, [contenteditable="true"]'),
  );
}

function onItemArrowKey(event) {
  if (event.altKey || event.ctrlKey || event.metaKey) return;
  const direction = shopInventoryArrowDirection(event.key, window.innerWidth);
  if (!direction) return;
  if (arrowInField(event.target) || arrowInField(document.activeElement)) return;
  if (editing.value || creating.value || !selectedId.value) return;
  if (photoLoader.value || saving.value || photoUploading.value || photoBusy.value) {
    return;
  }
  event.preventDefault();
  stepItem(direction);
}

function cancelItemSwipe() {
  itemSwipe = null;
}

function onEscape() {
  if (photoLoader.value) {
    photoLoader.value = false;
    return;
  }
  query.value = '';
  if (!editing.value) {
    creating.value = false;
    selectedId.value = null;
    revokePhoto();
    openDefaultItem();
  }
  focusFilter();
}

function openDefaultItem() {
  if (editing.value || creating.value || selectedId.value) return;
  const row = shopInventoryDefaultItem(items.value, query.value);
  if (row) selectItem(row);
}

function shopPhotoStamp(id) {
  const row = items.value.find((itemRow) => Number(itemRow.id) === Number(id));
  return row && row.photoFile ? String(row.photoFile) : '';
}

function rememberShopPhotoFile(id, photoFile) {
  const row = items.value.find((itemRow) => Number(itemRow.id) === Number(id));
  if (!row) return;
  row.hasPhoto = Boolean(photoFile);
  row.photoFile = photoFile || null;
}

function revokePhoto() {
  if (photoUrl.value) URL.revokeObjectURL(photoUrl.value);
  photoUrl.value = '';
}

async function loadPhoto(id) {
  const token = ++photoLoadToken;
  revokePhoto();
  if (!id) return;
  const stamp = shopPhotoStamp(id);
  if (!stamp) {
    shopPhotos.removeCachedPhoto(id);
    return;
  }
  let showedCache = false;
  try {
    const cached = await shopPhotos.getCachedPhotosForWorkOrder(id);
    if (token !== photoLoadToken) return;
    const hit =
      cached.find((photo) => String(photo.modifiedOn || '') === stamp) ||
      cached[0];
    if (hit && hit.blob) {
      photoUrl.value = URL.createObjectURL(hit.blob);
      showedCache = true;
      if (String(hit.modifiedOn || '') === stamp) {
        shopPhotos.touchCachedPhotos([id]);
        return;
      }
    }
  } catch {
    /* fetch the photo when the cache cannot be read */
  }
  try {
    const response = await axios.get(`/api/shop-inventory/${id}/photo`, {
      responseType: 'blob',
    });
    if (token !== photoLoadToken) return;
    const blob = response.data;
    if (!blob || !blob.size) return;
    try {
      await shopPhotos.putCachedPhoto({
        id,
        workOrderId: Number(id),
        modifiedOn: stamp,
        blob,
      });
    } catch {
      /* show the photo even if the device cache rejects the write */
    }
    if (token !== photoLoadToken) return;
    if (photoUrl.value) revokePhoto();
    photoUrl.value = URL.createObjectURL(blob);
  } catch {
    if (token !== photoLoadToken) return;
    if (!showedCache) photoUrl.value = '';
  }
}

async function prefetchShopPhotos() {
  const queue = items.value.filter(
    (row) => row && row.photoFile && Number(row.id) !== Number(selectedId.value),
  );
  for (const row of queue) {
    if ((await shopPhotos.cachedPhotoCount()) >= PHOTO_CACHE_LIMIT) return;
    if (Number(row.id) === Number(selectedId.value)) continue;
    try {
      const existing = await shopPhotos.getCachedPhotosForWorkOrder(row.id);
      if (
        existing.some(
          (photo) => String(photo.modifiedOn || '') === String(row.photoFile),
        )
      ) {
        continue;
      }
      const response = await axios.get(`/api/shop-inventory/${row.id}/photo`, {
        responseType: 'blob',
      });
      const blob = response.data;
      if (!blob || !blob.size) continue;
      await shopPhotos.putCachedPhotoPrefill({
        id: row.id,
        workOrderId: Number(row.id),
        modifiedOn: row.photoFile,
        blob,
      });
    } catch {
      /* the next item can still be cached */
    }
  }
}

async function load() {
  loading.value = true;
  error.value = '';
  try {
    const [listResult, buildingResult] = await Promise.all([
      axios.get('/api/shop-inventory'),
      axios.get('/api/shop-inventory/buildings'),
    ]);
    buildings.value = Array.isArray(buildingResult.data) ? buildingResult.data : [];
    items.value = Array.isArray(listResult.data) ? listResult.data : [];
    nextTick(() => prefetchShopPhotos());
  } catch (err) {
    error.value =
      err.response?.data?.error || 'Could not load shop inventory';
    items.value = [];
  } finally {
    loading.value = false;
  }
}

function fillDraft(row) {
  draft.value = {
    id: row.id || null,
    description: row.description || '',
    qty: shopQty(row.qty),
    inventoryDate: row.inventoryDate || '',
    buildingId: resolveDraftBuilding(row.buildingId),
    rack: shopPlaceCode(row.rack),
    shelf: shopPlaceCode(row.shelf),
    box: shopPlaceCode(row.box),
    notes: row.notes || '',
    hasPhoto: Boolean(row.hasPhoto),
  };
}

async function selectItem(row) {
  creating.value = false;
  editing.value = false;
  pendingPhoto.value = null;
  saveError.value = '';
  selectedId.value = row.id;
  fillDraft(row);
  await loadPhoto(row.id);
}

function openNew() {
  revokePhoto();
  pendingPhoto.value = null;
  saveError.value = '';
  selectedId.value = null;
  creating.value = true;
  editing.value = true;
  draft.value = blankDraft(readShopPlaceMemory(localStorage));
  photoLoader.value = true;
}

async function changeQty(delta) {
  const next = shopQty(Number(draft.value.qty) + delta);
  draft.value = { ...draft.value, qty: next };
  if (!editing.value && selectedId.value) await persistQty(next);
}

async function onQtyInput(event) {
  const next = shopQty(event.target.value);
  draft.value = { ...draft.value, qty: next };
  event.target.value = String(next);
  if (!editing.value && selectedId.value) await persistQty(next);
}

function onPlaceInput(field, value) {
  draft.value = { ...draft.value, [field]: placeCode(value) };
  rememberNewPlace();
}

async function onBuildingChange(event) {
  const buildingId = resolveDraftBuilding(event.target.value);
  draft.value = { ...draft.value, buildingId };
  rememberNewPlace();
  if (!editing.value && selectedId.value) await persistBuilding(buildingId);
}

async function persistBuilding(buildingId) {
  try {
    await axios.put(`/api/shop-inventory/${selectedId.value}`, { buildingId });
    const row = items.value.find((itemRow) => itemRow.id === selectedId.value);
    if (row) row.buildingId = buildingId;
  } catch (err) {
    saveError.value = err.response?.data?.error || 'Could not update building';
  }
}

async function persistQty(qty) {
  try {
    await axios.put(`/api/shop-inventory/${selectedId.value}`, { qty });
    const row = items.value.find((itemRow) => itemRow.id === selectedId.value);
    if (row) row.qty = qty;
  } catch (err) {
    saveError.value = err.response?.data?.error || 'Could not update quantity';
  }
}

function bodyFromDraft() {
  return {
    description: shopText(draft.value.description, DESCRIPTION_LIMIT).trim(),
    qty: shopQty(draft.value.qty),
    inventoryDate: draft.value.inventoryDate || shopInventoryToday(),
    buildingId: resolveDraftBuilding(draft.value.buildingId),
    rack: shopPlaceCode(draft.value.rack),
    shelf: shopPlaceCode(draft.value.shelf),
    box: shopPlaceCode(draft.value.box),
    notes: draft.value.notes || '',
  };
}

async function uploadPhoto(itemId, file) {
  const prepared = await prepareImageForWorkOrderUpload(file);
  const response = await uploadFileLikeWorkOrder(prepared.file, prepared.isHeic, {
    singleUrl: `/api/shop-inventory/${itemId}/photo`,
    initUrl: '/api/shop-inventory/photos/chunked/init',
    initBody: { itemId },
    chunkUrl: '/api/shop-inventory/photos/chunked/upload',
    finalizeUrl: '/api/shop-inventory/photos/chunked/finalize',
  });
  const photoFile = response && response.data && response.data.photoFile;
  if (photoFile) rememberShopPhotoFile(itemId, photoFile);
  return photoFile;
}

async function onPhotoPicked(event) {
  const file = event.target.files && event.target.files[0];
  event.target.value = '';
  if (!file) return;
  photoLoader.value = false;
  if (!isLikelyImageFile(file)) {
    saveError.value = 'Please select an image file';
    return;
  }
  if (!draft.value.id) {
    pendingPhoto.value = file;
    revokePhoto();
    photoUrl.value = URL.createObjectURL(file);
    return;
  }
  photoUploading.value = true;
  saveError.value = '';
  try {
    await uploadPhoto(draft.value.id, file);
    draft.value.hasPhoto = true;
    await loadPhoto(draft.value.id);
  } catch (err) {
    saveError.value = err.response?.data?.error || 'Could not save photo';
  } finally {
    photoUploading.value = false;
  }
}

async function saveItem() {
  const body = bodyFromDraft();
  if (!body.description) {
    saveError.value = 'Description is required';
    return;
  }
  saving.value = true;
  savingRecord.value = true;
  saveError.value = '';
  try {
    const wasNew = !draft.value.id;
    let id = draft.value.id;
    if (id) {
      await axios.put(`/api/shop-inventory/${id}`, body);
    } else {
      const { data } = await axios.post('/api/shop-inventory', body);
      id = data.id;
    }
    if (wasNew) writeShopPlaceMemory(localStorage, body);
    if (pendingPhoto.value) {
      photoUploading.value = true;
      try {
        await uploadPhoto(id, pendingPhoto.value);
        pendingPhoto.value = null;
      } finally {
        photoUploading.value = false;
      }
    }
    await load();
    const row = items.value.find((itemRow) => itemRow.id === id);
    if (row) await selectItem(row);
    editing.value = false;
    creating.value = false;
  } catch (err) {
    saveError.value = err.response?.data?.error || 'Could not save shop item';
  } finally {
    saving.value = false;
    savingRecord.value = false;
  }
}

async function deletePhoto() {
  if (!canClearPhoto.value) return;
  saveError.value = '';
  if (!draft.value.id) {
    pendingPhoto.value = null;
    revokePhoto();
    photoLoader.value = false;
    return;
  }
  photoBusy.value = true;
  try {
    await axios.delete(`/api/shop-inventory/${draft.value.id}/photo`);
    pendingPhoto.value = null;
    draft.value.hasPhoto = false;
    rememberShopPhotoFile(draft.value.id, null);
    shopPhotos.removeCachedPhoto(draft.value.id);
    revokePhoto();
    photoLoader.value = false;
  } catch (err) {
    saveError.value = err.response?.data?.error || 'Could not delete photo';
  } finally {
    photoBusy.value = false;
  }
}

async function deleteItem() {
  if (!draft.value.id) return;
  saving.value = true;
  saveError.value = '';
  try {
    const removedId = draft.value.id;
    await axios.delete(`/api/shop-inventory/${removedId}`);
    shopPhotos.removeCachedPhoto(removedId);
    selectedId.value = null;
    creating.value = false;
    editing.value = false;
    revokePhoto();
    await load();
    focusFilter();
  } catch (err) {
    saveError.value = err.response?.data?.error || 'Could not delete shop item';
  } finally {
    saving.value = false;
  }
}

onMounted(() => {
  load();
  focusFilter();
  window.addEventListener('keydown', onItemArrowKey, true);
});

onUnmounted(() => {
  window.removeEventListener('keydown', onItemArrowKey, true);
  clearPhotoPress();
  revokePhoto();
});

defineExpose({ reload: load, openNew, focusFilter });
</script>

<style scoped>
.shop-panel {
  flex: 1;
  min-height: 0;
  min-width: 0;
  margin-left: 5px;
  margin-right: 5px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background: #f3f3f3;
  container-type: inline-size;
}

.shop-filter-wrap {
  position: relative;
  flex: 0 0 auto;
  padding: 10px 10px 0;
}

.shop-filter {
  width: 100%;
  box-sizing: border-box;
  font: inherit;
  font-size: 0.95em;
  border: 1px solid #c9b3d2;
  border-radius: 8px;
  padding: 14px 40px 14px 16px;
  background: #fff;
  outline: none;
}

.shop-filter:focus,
.shop-filter:focus-visible {
  outline: none;
  border-color: #c9b3d2;
  box-shadow: none;
}

.shop-swipe-hint {
  margin: 0;
  text-align: center;
  font-size: 0.75rem;
  font-weight: 400;
  line-height: 1.2;
  color: #b0b0b0;
}

.shop-filter-clear {
  position: absolute;
  top: 10px;
  bottom: 0;
  right: 18px;
  margin-top: auto;
  margin-bottom: auto;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 14px;
  background: #907793;
  color: #fff;
  cursor: pointer;
}

.shop-matches,
.shop-detail {
  flex: 1;
  min-height: 0;
  min-width: 0;
}

.shop-matches,
.shop-detail-scroll {
  overflow-x: hidden;
  overflow-y: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.shop-matches {
  flex: 1;
  min-height: 0;
}

.shop-detail {
  overflow: hidden;
  display: flex;
  flex-direction: column;
  touch-action: pan-y;
}

.shop-detail-scroll {
  flex: 1;
  min-height: 0;
  min-width: 0;
}

.shop-matches::-webkit-scrollbar,
.shop-detail-scroll::-webkit-scrollbar {
  display: none;
}

.shop-dock {
  flex: 0 0 auto;
  padding: 8px 10px 10px;
  background: #f3f3f3;
}

.shop-dock .shop-status {
  margin: 0 0 8px;
}

.shop-hit {
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 48px;
  box-sizing: border-box;
  text-align: left;
  border: 1px solid #e0d6e4;
  background: #faf7fb;
  padding: 8px 10px;
  border-radius: 6px;
  cursor: pointer;
  overflow: hidden;
}

.shop-hit-alt {
  background: #ede4f0;
}

.shop-hit-text {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 600;
  color: #3d3142;
}

.shop-photo-frame {
  position: relative;
  flex: 0 0 368px;
  height: 368px;
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #e0d6e4;
  border-radius: 8px;
  background: #faf7fb;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  touch-action: pan-y;
  user-select: none;
}

.shop-spinner-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background: rgba(250, 247, 251, 0.86);
  color: #6e5875;
  font-weight: 700;
}

.shop-spinner-overlay i {
  font-size: 2.4rem;
  color: #907793;
}

.shop-id-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  width: 100%;
  max-width: 100%;
  min-width: 0;
}

.shop-id-cell {
  flex: 0 0 40%;
  width: 40%;
  max-width: 40%;
  min-width: 0;
  box-sizing: border-box;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 2px;
  color: #6e5875;
  font-size: 0.75em;
}

.shop-id-cell strong {
  font-size: 1.6rem;
  line-height: 1.1;
  color: #3d3142;
  overflow: hidden;
  text-overflow: ellipsis;
}

.shop-date-cell {
  margin-left: auto;
  align-items: flex-end;
  text-align: right;
}

.shop-date-cell input {
  display: block;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  font-size: 1rem;
  color: #3d3142;
  border: 1px solid #c9b3d2;
  border-radius: 8px;
  padding: 8px 6px;
  background: #fff;
  text-align: right;
}

.shop-photo {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.shop-no-photo {
  color: #6e5875;
  font-size: 1.1em;
}

.shop-photo-loader {
  position: fixed;
  inset: 0;
  z-index: 30;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 16px;
  background: rgba(40, 32, 48, 0.72);
}

.shop-photo-loader-card {
  width: min(420px, 100%);
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px;
  border-radius: 12px;
  background: #fff;
}

.shop-photo-pick {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 48px;
  border: none;
  border-radius: 8px;
  background: #907793;
  color: #fff;
  font-weight: 600;
  cursor: pointer;
}

.shop-photo-delete {
  background: #6e5875;
}

.shop-photo-pick input {
  display: none;
}

.shop-photo-pick:disabled {
  opacity: 0.6;
}

.qty-row {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  max-width: 100%;
  min-width: 0;
}

.qty-btn {
  flex: 1 1 0;
  min-width: 0;
  height: 64px;
  border: none;
  border-radius: 8px;
  background: #907793;
  color: #fff;
  font-size: 2rem;
  line-height: 1;
  cursor: pointer;
}

.qty-input {
  flex: 0 0 88px;
  width: 88px;
  max-width: 88px;
  min-width: 0;
  height: 64px;
  box-sizing: border-box;
  text-align: center;
  font-size: 1.6rem;
  font-weight: 700;
  border: 1px solid #c9b3d2;
  border-radius: 8px;
  background: #fff;
  color: #3d3142;
}

.shop-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  color: #6e5875;
  font-size: 0.8em;
}

.shop-field textarea,
.shop-field input,
.place-cell input {
  font: inherit;
  font-size: 1rem;
  color: #3d3142;
  border: 1px solid #c9b3d2;
  border-radius: 8px;
  padding: 8px 10px;
  background: #fff;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
}

.shop-value,
.shop-notes {
  margin: 0;
  color: #3d3142;
  font-size: 1rem;
  white-space: pre-wrap;
}

.shop-field textarea,
.shop-value {
  box-sizing: border-box;
  min-height: 6.5rem;
  border: 1px solid rgba(110, 88, 117, 0.22);
  border-radius: 8px;
  padding: 8px 10px;
}

.place-row {
  display: flex;
  align-items: flex-end;
  justify-content: center;
  gap: 4px;
  width: 100%;
  max-width: 100%;
  min-width: 0;
}

.place-cell.place-building {
  flex: 0 0 12.75rem;
  width: 12.75rem;
  max-width: 12.75rem;
  min-width: 0;
  align-items: stretch;
}

.place-building select {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  font: inherit;
  font-size: 0.8rem;
  font-weight: 600;
  color: #3d3142;
  border: 1px solid #c9b3d2;
  border-radius: 8px;
  /* Rack input is 1.4rem with 8px vertical padding. Keep 0.8rem type
     and add half that font difference as padding so the boxes match. */
  padding: calc(8px + (1.4rem - 0.8rem) / 2) 22px calc(8px + (1.4rem - 0.8rem) / 2) 10px;
  background: #fff;
}

/* The shop column stays ~440px inside a wider desktop window. Drop the
   old 32px left inset and give that width to the building select so its
   left edge lines up with the description field. */
@container (max-width: 520px) {
  .place-row {
    justify-content: flex-start;
    box-sizing: border-box;
  }

  .place-cell.place-building {
    flex: 1 1 10.5rem;
    width: auto;
    max-width: calc(11.25rem + 32px);
    min-width: 8.5rem;
  }

  .place-cell:not(.place-building) {
    flex: 0 1 4.25rem;
    width: auto;
    min-width: 2.5rem;
  }
}

.place-cell {
  flex: 0 0 4.25rem;
  width: 4.25rem;
  max-width: 4.25rem;
  min-width: 0;
  box-sizing: border-box;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  color: #6e5875;
  font-size: 0.75em;
}

.place-cell strong {
  font-size: 2rem;
  line-height: 1.1;
  color: #3d3142;
}

.place-cell input {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  text-align: center;
  font-size: 1.4rem;
  font-weight: 700;
  padding: 8px 4px;
}

.shop-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  min-height: 48px;
  border: none;
  border-radius: 8px;
  background: #907793;
  color: #fff;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
}

.shop-edit-actions {
  display: flex;
  gap: 8px;
}

.shop-delete {
  background: #6e5875;
}

.shop-status {
  margin: 8px 10px;
  color: #6e5875;
}

.shop-action:disabled,
.qty-btn:disabled {
  opacity: 0.6;
}
</style>
